import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Info, ArrowRight } from 'lucide-react';
import WALink from '../components/WALink';

const products = [
  {
    id: 1, name: 'Gulzar Co-ord Set', category: 'Co-ord Sets',
    fabric: 'Pure Cotton', stitches: 'Murri & Phanda work',
    desc: 'Matching kurta-trouser co-ord with allover chikankari. Light, breathable, all-day elegance.',
    price: '₹2,800 onwards',
    img: 'https://images.unsplash.com/photo-1733209589780-99d3a8a3b3e4?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
    tag: 'Bestseller',
  },
  {
    id: 2, name: 'Bahar Long Kurti', category: 'Long Kurtas',
    fabric: 'Kota Doria', stitches: 'Jaali & Bakhiya work',
    desc: 'Floor-grazing long kurti in luxurious Kota Doria with intricate jaali embroidery.',
    price: '₹3,200 onwards',
    img: 'https://images.unsplash.com/photo-1645389776186-725084abd3fc?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
    tag: 'New Arrival',
  },
  {
    id: 3, name: 'Nawabi Angarkha', category: 'Angarkha',
    fabric: 'Chanderi Silk', stitches: 'Kangan & Tepchi work',
    desc: 'Mughal-inspired angarkha in pure Chanderi silk. The perfect fusion of heritage and high fashion.',
    price: '₹4,500 onwards',
    img: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
    tag: 'Heritage Pick',
  },
  {
    id: 4, name: 'Riyasat Suit Set', category: 'Suit Sets',
    fabric: 'Mal Mal Doria', stitches: 'Shadow & Murri work',
    desc: 'Three-piece suit — kurta, dupatta & salwar — in ethereal Mal Mal Doria. Grace, every day.',
    price: '₹3,800 onwards',
    img: 'https://images.pexels.com/photos/20788490/pexels-photo-20788490.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    tag: null,
  },
  {
    id: 5, name: 'Naazuk Short Top', category: 'Short Tops',
    fabric: 'Viscose Chiffon', stitches: 'Phanda & Bakhiya work',
    desc: 'Breezy short kurti-top in soft viscose chiffon. Pair with palazzos, jeans, or skirts — your story.',
    price: '₹1,600 onwards',
    img: 'https://images.unsplash.com/photo-1652953186433-70052f26c183?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
    tag: 'GenZ Fave',
  },
  {
    id: 6, name: 'Zara Crocia Dress', category: 'Crocia',
    fabric: 'Pure Silk', stitches: 'Crocia & Murri work',
    desc: 'Artisan crochet-chikankari fusion in pure silk. Rare, intricate, utterly one-of-a-kind.',
    price: '₹5,200 onwards',
    img: 'https://images.unsplash.com/photo-1742742459138-253e807f2ed2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
    tag: 'Limited',
  },
  {
    id: 7, name: 'Saheli Chiffon Suit', category: 'Suit Sets',
    fabric: 'Pure Chiffon', stitches: 'Tepchi & Shadow work',
    desc: 'Dreamy chiffon suit with delicate shadow work and tepchi embroidery. Light as a breeze, beautiful as poetry.',
    price: '₹4,000 onwards',
    img: 'https://images.pexels.com/photos/28512773/pexels-photo-28512773.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    tag: null,
  },
  {
    id: 8, name: 'Mehfil Long Kurti', category: 'Long Kurtas',
    fabric: 'Mal Mal Doria', stitches: 'Bakhiya & Jaali work',
    desc: 'Flowy long kurti with allover bakhiya embroidery. From morning chai to evening mehfil — she\'s versatile.',
    price: '₹2,400 onwards',
    img: 'https://images.pexels.com/photos/28512776/pexels-photo-28512776.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    tag: null,
  },
];

const CATEGORIES = ['All', 'Co-ord Sets', 'Long Kurtas', 'Angarkha', 'Suit Sets', 'Short Tops', 'Crocia'];

const tagColors = {
  'Bestseller': 'bg-[#2D4A2D] text-[#F5F0E8]',
  'New Arrival': 'bg-[#C9A84C] text-[#1A2A1A]',
  'Heritage Pick': 'bg-[#1A2A1A] text-[#F5F0E8]',
  'GenZ Fave': 'bg-[#C9A84C] text-[#1A2A1A]',
  'Limited': 'bg-red-700 text-white',
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

export default function CataloguePage() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? products
    : products.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#F5F0E8] pt-16 md:pt-20">
      {/* Page Hero */}
      <div className="bg-[#2D4A2D] py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: `radial-gradient(circle at 1px 1px, #C9A84C 1px, transparent 0)`, backgroundSize: '28px 28px' }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-[#F5F0E8]/60 font-sans text-sm hover:text-[#C9A84C] transition-colors mb-8 group">
            <ArrowLeft size={16} strokeWidth={1.5} className="group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
          <span className="block text-xs font-sans uppercase tracking-[0.25em] text-[#C9A84C] font-medium mb-3">
            Handcrafted with Love
          </span>
          <h1 className="font-serif font-light text-[#F5F0E8] text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight mb-4">
            The Collection
          </h1>
          <p className="font-sans font-light text-[#F5F0E8]/70 text-base max-w-xl leading-relaxed">
            Each piece authentically hand-embroidered in Lucknow. Available in Pure Cotton, Kota Doria, Mal Mal, Chanderi, Silk, Chiffon & Viscose.
          </p>
        </div>
      </div>

      {/* Reference Disclaimer — prominent */}
      <div className="bg-[#C9A84C]/15 border-y border-[#C9A84C]/30 py-3.5 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex items-start gap-3">
          <Info size={16} strokeWidth={1.5} className="text-[#C9A84C] flex-shrink-0 mt-0.5" />
          <p className="font-sans font-light text-[#2D4A2D] text-xs md:text-sm leading-relaxed">
            <strong className="font-medium">Images are for reference purpose only.</strong> Our catalogue is frequently updated with new designs, fabrics & colourways.
            DM us on WhatsApp or Instagram for the latest available pieces and custom requests.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-10 pb-6 border-b border-[#D5CCBF]">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              data-testid={`filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
              className={`px-4 py-2 font-sans text-xs font-medium uppercase tracking-[0.15em] transition-all duration-200 border ${
                activeCategory === cat
                  ? 'bg-[#2D4A2D] text-[#F5F0E8] border-[#2D4A2D]'
                  : 'bg-transparent text-[#2D4A2D] border-[#D5CCBF] hover:border-[#2D4A2D]'
              }`}
            >
              {cat}
            </button>
          ))}
          <span className="ml-auto font-sans text-xs text-[#5C6B5C] self-center">
            {filtered.length} piece{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {filtered.map((product, i) => (
            <motion.div
              key={product.id}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-40px' }}
              transition={{ delay: (i % 4) * 0.08 }}
              className="group"
              data-testid={`product-card-${product.id}`}
            >
              {/* Image */}
              <div className="relative overflow-hidden aspect-[3/4] mb-4 bg-[#E8DED1]">
                <img
                  src={product.img}
                  alt={`${product.name} — ${product.fabric} Chikankari`}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Tag */}
                {product.tag && (
                  <span className={`absolute top-3 left-3 px-2.5 py-1 text-[10px] font-sans font-medium uppercase tracking-wider ${tagColors[product.tag] || 'bg-[#2D4A2D] text-[#F5F0E8]'}`}>
                    {product.tag}
                  </span>
                )}
                {/* Hover CTA */}
                <div className="absolute inset-0 bg-[#1A2A1A]/0 group-hover:bg-[#1A2A1A]/55 transition-all duration-400 flex items-end justify-center pb-6">
                  <WALink
                    source={`catalogue_${product.name}`}
                    className="opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-300 flex items-center gap-2 bg-[#C9A84C] text-[#1A2A1A] px-5 py-2.5 font-sans font-medium text-xs tracking-wide"
                  >
                    <MessageCircle size={14} strokeWidth={1.5} />
                    Enquire on WhatsApp
                  </WALink>
                </div>
              </div>

              {/* Info */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-sans uppercase tracking-[0.2em] text-[#C9A84C] font-medium">{product.category}</span>
                  <span className="text-[10px] font-sans text-[#5C6B5C]">{product.fabric}</span>
                </div>
                <h3 className="font-serif text-lg text-[#2D4A2D] leading-tight mb-1">{product.name}</h3>
                <p className="font-sans font-light text-[#5C6B5C] text-xs leading-snug mb-1">{product.desc}</p>
                <p className="text-[10px] font-sans text-[#5C6B5C] italic mb-3">{product.stitches}</p>
                <WALink
                  source={`catalogue_card_${product.id}`}
                  className="w-full flex items-center justify-center gap-2 border border-[#2D4A2D] text-[#2D4A2D] py-2.5 font-sans text-xs font-medium tracking-wide hover:bg-[#2D4A2D] hover:text-[#F5F0E8] transition-all duration-200"
                >
                  <MessageCircle size={13} strokeWidth={1.5} />
                  Enquire on WhatsApp
                </WALink>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center border-t border-[#D5CCBF] pt-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-px w-12 bg-[#C9A84C]" />
            <span className="text-xs font-sans uppercase tracking-[0.25em] text-[#C9A84C] font-medium">Can't find your style?</span>
            <div className="h-px w-12 bg-[#C9A84C]" />
          </div>
          <h3 className="font-serif font-light text-[#2D4A2D] text-3xl md:text-4xl mb-4">
            We do <span className="italic">custom orders</span> too
          </h3>
          <p className="font-sans font-light text-[#5C6B5C] text-sm max-w-md mx-auto mb-8 leading-relaxed">
            Apni pasand ka design, apni pasand ka fabric — hum banayenge. DM us or fill the bulk enquiry form.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <WALink
              source="catalogue_bottom_cta"
              className="inline-flex items-center justify-center gap-2.5 bg-[#2D4A2D] text-[#F5F0E8] px-8 py-4 font-sans font-medium text-sm tracking-wide transition-all duration-300 hover:bg-[#1A2A1A] hover:text-[#C9A84C]"
            >
              <MessageCircle size={18} strokeWidth={1.5} />
              WhatsApp for Custom Order
            </WALink>
            <Link
              to="/#enquiry"
              onClick={() => { setTimeout(() => document.getElementById('enquiry')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
              className="inline-flex items-center justify-center gap-2 border border-[#2D4A2D] text-[#2D4A2D] px-8 py-4 font-sans font-medium text-sm tracking-wide transition-all duration-300 hover:bg-[#2D4A2D] hover:text-[#F5F0E8] group"
            >
              Bulk Order Enquiry
              <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
